import { parserMoney, postRequest } from "../../../../Utils/Functions";
import { useEffect, useState } from "react";
import { gql } from "@apollo/client/core";
import { useQuery } from "@apollo/client";
import fetcher from "src/utils/fetcher";
import { MAX_YEARS, exceedsLongTermMaxLoan } from "../plazos";

const QUERY_PRICE = gql`
	query property($id: ID) {
		property(id: $id) {
			price {
				currency {
					name
				}
				amount
			}
		}
	}
`;

const ITAU_SERVICE = "https://prod.infocasas.com.uy/microservices/simulador/?/itau/simulation";
const ITAU_GET_VALUES =
	"https://prod.infocasas.com.uy/microservices/simulador/?/itau/getValuesProperty/";

// La cuota se le pide a Itaú desde el navegador (INF-1974): su firewall bloquea a 4 de
// los 5 servers de IC2, y su API acepta CORS sólo desde www.infocasas.com.uy. En
// cualquier otro origen (localhost, beta, casaseneleste), o si Itaú no contesta a
// tiempo, se usa el proxy de IC2. Los campos y defaults son los mismos que arma
// `ItauCalculationController` en infocasas2_0: si cambian, cambian en los dos lados.
const ITAU_CALCULATE_URL = "https://www.itau.com.uy/pub/prestamos/hipotecario/calcular";
const ITAU_CORS_ORIGIN = "https://www.infocasas.com.uy";
const ITAU_TIMEOUT_MS = 8000;

const fetchItauDirect = async params => {
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), ITAU_TIMEOUT_MS);
	try {
		const response = await fetch(ITAU_CALCULATE_URL, {
			method: "POST",
			signal: controller.signal,
			body: new URLSearchParams(params),
		});
		return response.ok ? await response.json() : null;
	} catch (e) {
		return null; // red o timeout
	} finally {
		clearTimeout(timeout);
	}
};

const fetchItauCalculation = async ({ propertyValue, loanAmount, currencyCode, termYears }) => {
	if (typeof window !== "undefined" && window.location.origin === ITAU_CORS_ORIGIN) {
		const data = await fetchItauDirect({
			montoDelBien: String(propertyValue),
			montoDelPrestamo: String(loanAmount),
			codigoMoneda: currencyCode,
			aniosDePlazo: String(termYears),
			destino: "1",
			tipoPersona: "F",
		});
		if (data) return data;
	}
	return fetcher<any>(`${process.env.graphQL_uri?.replace("/graphql", "")}/api/1.0/itau/calculate`, {
		method: "POST",
		body: JSON.stringify({
			propertyValue,
			loanAmount,
			currencyCode,
			termYears,
			purpose: 1,
			personType: "F",
		}),
	});
};

const TYPES = [
	{
		value: "UI",
		text: "Unidades Indexadas",
	},
	{
		value: "USD",
		text: "Dolares",
	},
];

export const useItauFinancingV2 = ({ id, source, showFinancingCard }) => {
	const { data: propertyData } = useQuery(QUERY_PRICE, { variables: { id } });
	const [loan, setLoan] = useState(0);
	const [propertyPrice, setPropertyPrice] = useState(0);
	const [propertyCurrency, setPropertyCurrency] = useState(undefined);
	const [years, setYears] = useState(20);
	const [type, setType] = useState(TYPES[0].value);
	const [payment, setPayment] = useState(0);
	const [matchedYears, setMatchedYears] = useState([]);
	const [, setIsDefaultSimulation] = useState(true);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	let timer = null;

	const usdYearsOptions = Array.from(Array(MAX_YEARS.USD), (e, i) => i + 1);
	const uiYearsOptions = Array.from(Array(MAX_YEARS.UI), (e, i) => i + 1);
	const overLongTermMaxLoan = exceedsLongTermMaxLoan(type, years, loan, propertyCurrency);
	const withinLimits =
		loan >= 40_000 &&
		Math.round((loan * 100) / propertyPrice) <= 80 &&
		years <= MAX_YEARS[type] &&
		!overLongTermMaxLoan;
	const canShowPayment = Boolean(payment) && withinLimits && !loading && !error;

	const YEARS = matchedYears.map(year => ({
		value: year,
		text: `${year} año${year > 1 ? "s" : ""}`,
	}));

	useEffect(() => {
		if (type === "UI") {
			setMatchedYears(uiYearsOptions);
		} else if (type === "USD") {
			setMatchedYears(usdYearsOptions);
			if (years > MAX_YEARS.USD) {
				setYears(MAX_YEARS.USD);
			}
		}
	}, [type]);

	useEffect(() => {
		if (propertyData && propertyData.property) {
			const amount = propertyData.property.price?.amount ?? 0;
			setPropertyPrice(amount);
			setPropertyCurrency(propertyData?.property?.price?.currency?.name);
			setLoan(Math.round(amount * 0.8));
		}
	}, [propertyData]);

	useEffect(() => {
		// Si los datos cambian antes de que vuelva la respuesta, ésta se descarta: si no,
		// la cuota de un pedido viejo quedaría al lado de los datos nuevos.
		let stale = false;
		const getValues = async () => {
			setLoading(true);
			setError(null);
			try {
				const data = await fetchItauCalculation({
					propertyValue: propertyPrice,
					loanAmount: loan,
					currencyCode: type,
					termYears: years,
				});
				if (stale) return;
				if (!data?.detalleCotizacion) throw new Error(data?.error ?? "Itaú no devolvió cotización");
				if (type === "UI") {
					setPayment(parserMoney(data?.detalleCotizacion?.cuota_referencia_UYU));
				} else if (type === "USD") {
					setPayment(parserMoney(data?.detalleCotizacion?.cuota_referencia_USD));
				}
				// setPropertyPrice(parserMoney(propertyPrice));
				// setLoan(parserMoney(data.detalleCotizacion.monto_solicitado));
				// setYears(data.detalleCotizacion.plazo / 12);
				// setType(data.detalleCotizacion.destino);
				// setAsset(data.detalleCotizacion.financiacion);
				// setIsDefaultSimulation(true);
			} catch (error) {
				if (!stale) setError(error);
				return;
			} finally {
				if (!stale) setLoading(false);
			}
		};

		if (propertyPrice > 0 && withinLimits) {
			getValues();
		} else {
			setPayment(0);
			setError(null);
			setLoading(false);
		}
		return () => {
			stale = true;
		};
	}, [propertyPrice, loan, type, years, propertyCurrency]);

	return {
		loan: {
			amount: loan,
			change: setLoan,
		},
		property: {
			amount: propertyPrice,
		},
		years: {
			totalYears: YEARS,
			value: years,
			change: setYears,
		},
		type: {
			options: TYPES,
			value: type,
			change: setType,
		},
		setIsDefaultSimulation,
		payment,
		canShowPayment,
		overLongTermMaxLoan,
		propertyCurrency,
		show: true,
		loading,
		error,
	};
};

